import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import settingsService from '../services/settingsService';

export const useSystemSettings = () => {
  return useQuery({
    queryKey: ['system-settings'],
    queryFn: async () => {
      console.log('React Query: Fetching system settings...');
      const result = await settingsService.getAllSystemSettings();
      console.log('React Query: Received settings:', result);
      return result;
    },
  });
};

export const useUpdateSystemSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      settingsService.updateSystemSetting(key, value),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      console.log(`Setting ${variables.key} updated successfully`);
      // Don't show individual success messages - let the calling component handle it
    },
    onError: (error: any, variables) => {
      console.error(`Failed to update setting ${variables.key}:`, {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        key: variables.key,
        value: variables.value
      });
      // Don't show individual error messages - let the calling component handle it
    },
    retry: false, // Don't retry failed settings updates
  });
};

export const useDocumentSequences = (companyId: string | number | undefined) => {
  const key = companyId != null && companyId !== '' ? String(companyId) : '';
  return useQuery({
    queryKey: ['document-sequences', key],
    queryFn: () => settingsService.getAllDocumentSequences(key),
    enabled: !!key,
  });
};

export const useSaveDocumentSequence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      body,
    }: {
      companyId: string | number;
      body: {
        id?: number;
        document_type: string;
        prefix?: string;
        separator?: string;
        current_number?: number;
        padding_length?: number;
        include_year?: boolean;
        include_month?: boolean;
        reset_yearly?: boolean;
        reset_monthly?: boolean;
        is_active?: boolean;
      };
    }) => settingsService.upsertDocumentSequence(companyId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['document-sequences', String(variables.companyId)] });
    },
  });
};

export const useGenerateDocumentNumber = () => {
  return useMutation({
    mutationFn: ({ companyId, documentType }: { companyId: string | number; documentType: string }) =>
      settingsService.generateDocumentNumber(companyId, documentType),
  });
};

/** Per-tenant company profile (company_settings for logged-in company). */
export const useCompanyProfileSettings = (companyId: string | undefined) => {
  return useQuery({
    queryKey: ['company-profile-settings', companyId || ''],
    queryFn: () => settingsService.getCompanySettings(),
    enabled: !!companyId,
  });
};

export const useUpdateCompanyProfileSettings = (companyId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      settingsData: Record<string, string | { value: string; type?: string; category?: string }>
    ) => settingsService.updateCompanySettings(settingsData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-profile-settings', companyId || ''] });
    },
  });
};

